import React, { useEffect, useState, useRef } from "react";
import {
    Box, Grid, Card, CardContent, Typography, Chip, Avatar, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    FormControl, InputLabel, Select, MenuItem, Tooltip, IconButton,
    InputAdornment, LinearProgress, CircularProgress, Badge, Autocomplete, Divider,
} from "@mui/material";
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useTheme } from "@mui/material/styles";
import MaterialTable from "material-table";
import Base from "../../../Base/views/Base";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import CommonDatePicker from "../../../Base/views/CommonDatePicker";
import CustomToggle from "../../../Base/views/CustomToggle";
import { useDispatch, useSelector } from "react-redux";
import { GetAtsAgreementsAction, AddAtsAgreementAction, UpdateAtsAgreementAction, DeleteAtsAgreementAction, UploadAgreementSignedCopyAction } from "../../../../../store/ATS/Agreement/Action";
import { GenerateAgreementDocHelper, GetAgreementSignedUrlHelper, RequestFeeOverrideHelper, GetFeeOverrideRequestsHelper, RespondFeeOverrideHelper, GetAtsAgreementMastersHelper, GetAtsAgreementTemplatesHelper } from "../../../ATS/helper/AtsAgreementHelper";
import { GetReportingManagersHelper } from "../../../Employee/helper/EmployeeHelper";
import { GetAtsCompanyAction } from "../../../../../store/ATS/Company/Action";
import { GetAtsGroupCompanyAction } from "../../../../../store/ATS/GroupCompany/Action";
import { ConvertDate, getUserData, isAdminOwner } from "../../../Base/helper/baseFunctions";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "../../css/styles.css";

// ─── Constants ──────────────────────────────────────────────────────────────
const BRAND = "#8B44FF";
const filter = createFilterOptions();

const STATUS_STYLES = {
    "Active": { bg: "#D1FAE5", color: "#059669" },
    "Pending": { bg: "#FEF3C7", color: "#D97706" },
    "Expired": { bg: "#FEE2E2", color: "#DC2626" },
    "Completed": { bg: "#DBEAFE", color: "#2563EB" },
    "Draft": { bg: "#F1F5F9", color: "#64748B" },
};

const STATUS_OPTIONS = ["Active", "Pending", "Expired", "Completed", "Draft"];



const formatCurrency = (val) => {
    const n = Number(val) || 0;
    if (!n) return "—";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
    return `₹${n.toLocaleString("en-IN")}`;
};

const dateStr = (d) => d ? `${d.getFullYear()}-${("0" + (d.getMonth() + 1)).slice(-2)}-${("0" + d.getDate()).slice(-2)}` : "";

// ─── KPI Card ────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, value, label, color }) => (
    <Card sx={{ display: "block", overflow: "visible", cursor: "pointer" }}>
        <CardContent sx={{ padding: "12px !important" }}>
            <Box justifyContent="space-between" display="flex">
                <Typography sx={{ fontSize: 14 }} variant="subtitle2" color="primary.contrastText" gutterBottom>
                    {label}
                </Typography>
            </Box>
            <Box justifyContent="space-between" display="flex">
                <Typography textAlign="center" variant="h3" gutterBottom>
                    {value}
                </Typography>
                {icon && React.cloneElement(icon, {
                    className: "card-icon",
                    sx: { fontSize: "62px !important", height: "62px !important", width: "62px !important", color: color, opacity: 0.8, bottom: "-10px", right: "7px", position: "absolute" }
                })}
            </Box>
        </CardContent>
    </Card>
);

const EMPTY = { client_id: "", agreement_type: "", start_date: "", end_date: "", value: "", commission_pct: "", notes: "", status: "Active" };

// ─── Main Component ──────────────────────────────────────────────────────────
const AtsAgreements = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const user = getUserData();
    const isGroupOrg = user?.grouporg_flow === true;
    const isAdmin = isAdminOwner();

    const { atsAgreementData, isLoading, signedUploadLoading } = useSelector(s => s.AtsReducer?.AtsAgreementReducer);
    const { atsCompanyData } = useSelector(s => s.AtsReducer?.AtsCompanyReducer);
    const { atsGroupCompanyData } = useSelector(s => s.AtsReducer?.AtsGroupCompanyReducer);

    const [companyData, setCompanyData] = useState({ load: true, values: [] });
    const [templatesList, setTemplatesList] = useState([]);
    const [agreementTypes, setAgreementTypes] = useState([]);
    const [commissionOptions, setCommissionOptions] = useState([]);
    const [feeBasisOptions, setFeeBasisOptions] = useState([]);
    const [replacementPeriods, setReplacementPeriods] = useState([]);
    const [agreementDurations, setAgreementDurations] = useState([]);
    const [filterClient, setFilterClient] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [inactive, setInactive] = useState(false);
    const handleToggle = (e) => {
        setInactive(e);
    };
    const [openDialog, setOpenDialog] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [editId, setEditId] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [generateRow, setGenerateRow] = useState(null);
    const [adminWarning, setAdminWarning] = useState(false);

    // ── Validation states ────────────────────────────────────────────────────
    const [formErrors, setFormErrors] = useState({});

    const validateStep1 = () => {
        let temp = {};
        const clientId = isGroupOrg
            ? form.client_id?.ats_grouporg_id || form.client_id
            : form.client_id?.ats_company_id || form.client_id;
        temp.client_id = clientId ? "" : "Client is required";
        temp.agreement_type = form.agreement_type ? "" : "Agreement Type is required";
        temp.start_date = form.start_date ? "" : "Start Date is required";
        temp.period_months = form.period_months ? "" : "Agreement Duration is required";
        setFormErrors(temp);
        return Object.values(temp).every((x) => x === "");
    };

    const validateStep2 = () => {
        let temp = {};
        temp.commission_pct = form.commission_pct ? "" : "Professional Fee % is required";
        if (form.commission_pct === "custom") {
            temp.custom_fee = form.custom_fee ? "" : "Custom Fee % is required";
        }
        temp.fee_basis = form.fee_basis ? "" : "Fee Basis is required";
        temp.replacement_months = form.replacement_months ? "" : "Replacement Duration is required";
        temp.authority_name = form.authority_name?.trim() ? "" : "Signing Authority Name is required";

        // Dynamic custom prompts validation
        const activeTemplate = templatesList.find(t => t.agreement_type === form.agreement_type);
        if (activeTemplate) {
            let fields = activeTemplate.template_fields || null;
            if (fields && !Array.isArray(fields)) {
                fields = Object.values(fields);
            }
            if (!fields && activeTemplate.template_content) {
                const match = activeTemplate.template_content.match(/<!--FIELDS_JSON:(.*)-->/);
                if (match) {
                    try {
                        fields = JSON.parse(match[1]);
                    } catch (e) {}
                }
            }
            if (fields) {
                const standardNames = [
                    "client_name", "client_address", "client_gstin",
                    "org_name", "org_address", "org_city",
                    "authority_name", "authority_title", "authority_place",
                    "commission_pct", "replacement_months",
                    "replacement_days", "period_months", "start_date", "fee_basis"
                ];
                fields.forEach(f => {
                    if (!standardNames.includes(f.name) && f.required !== false) {
                        if (!form[f.name] || !String(form[f.name]).trim()) {
                            temp[f.name] = `${f.label} is required`;
                        }
                    }
                });
            }
        }

        setFormErrors(temp);
        return Object.values(temp).every((x) => x === "");
    };

    const handleFormChange = (field, value) => {
        const nextForm = { ...form, [field]: value };
        if (field === "commission_pct" || field === "custom_fee") {
            nextForm.admin_override = false;
            setAdminWarning(false);
        }
        setForm(nextForm);

        if (formErrors[field]) {
            setFormErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    const renderDynamicTemplatePrompts = () => {
        const activeTemplate = templatesList.find(t => t.agreement_type === form.agreement_type);
        if (!activeTemplate) return null;

        let fields = activeTemplate.template_fields || null;
        if (fields && !Array.isArray(fields)) {
            fields = Object.values(fields);
        }
        if (!fields && activeTemplate.template_content) {
            const match = activeTemplate.template_content.match(/<!--FIELDS_JSON:(.*)-->/);
            if (match) {
                try {
                    fields = JSON.parse(match[1]);
                } catch (e) {}
            }
        }
        if (!fields) return null;

        const standardNames = [
            "client_name", "client_address", "client_gstin",
            "org_name", "org_address", "org_city",
            "authority_name", "authority_title", "authority_place",
            "commission_pct", "replacement_months",
            "replacement_days", "period_months", "start_date", "fee_basis"
        ];

        const dynamicFields = fields.filter(f => !standardNames.includes(f.name));
        if (dynamicFields.length === 0) return null;

        return dynamicFields.map((field) => {
            const isRequired = field.required !== false;
            const errorMsg = formErrors[field.name] || "";

            if (field.options && Array.isArray(field.options) && field.options.length > 0) {
                return (
                    <Grid item xs={12} sm={6} key={field.name}>
                        <FormControl size="small" fullWidth required={isRequired} error={!!errorMsg}>
                            <InputLabel>{field.label}</InputLabel>
                            <Select
                                value={form[field.name] || ""}
                                label={field.label}
                                onChange={e => handleFormChange(field.name, e.target.value)}
                            >
                                {field.options.map(opt => (
                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                ))}
                            </Select>
                            {errorMsg && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{errorMsg}</Typography>}
                        </FormControl>
                    </Grid>
                );
            }

            return (
                <Grid item xs={12} sm={6} key={field.name}>
                    <TextField
                        size="small"
                        fullWidth
                        required={isRequired}
                        error={!!errorMsg}
                        helperText={errorMsg}
                        label={field.label}
                        type={field.type === "date" ? "date" : (field.type === "number" ? "number" : "text")}
                        InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
                        value={form[field.name] || ""}
                        onChange={e => handleFormChange(field.name, e.target.value)}
                    />
                </Grid>
            );
        });
    };

    const [overrideRequestId, setOverrideRequestId] = useState(null);
    const [overrideStatus, setOverrideStatus] = useState(null);
    const [overrideSending, setOverrideSending] = useState(false);
    const [approvalRequests, setApprovalRequests] = useState([]);
    const [adminApprovalOpen, setAdminApprovalOpen] = useState(false);
    const [activeRequest, setActiveRequest] = useState(null);
    const [signatories, setSignatories] = useState([]);

    const [signedCopyRow, setSignedCopyRow] = useState(null);
    const [signedFile, setSignedFile] = useState(null);
    const [signedUrlLoading, setSignedUrlLoading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        isGroupOrg ? dispatch(GetAtsGroupCompanyAction()) : dispatch(GetAtsCompanyAction());
        GetAtsAgreementTemplatesHelper().then((res) => {
            if (res && !res.hasError && res.data?.data) {
                setTemplatesList(res.data.data);
                setAgreementTypes(res.data.data.map(t => t.agreement_type));
            }
        });

        GetAtsAgreementMastersHelper("commission").then((res) => {
            if (res && !res.hasError && res.data?.data) {
                setCommissionOptions(res.data.data);
                if (res.data.data.length > 0) {
                    const item = res.data.data[0];
                    setForm(prev => ({
                        ...prev,
                        commission_pct: item.value_pct || item.value
                    }));
                }
            }
        });
        GetAtsAgreementMastersHelper("fee_basis").then((res) => {
            if (res && !res.hasError && res.data?.data) {
                setFeeBasisOptions(res.data.data.map(i => i.name));
                if (res.data.data.length > 0) {
                    const item = res.data.data[0];
                    setForm(prev => ({
                        ...prev,
                        fee_basis: item.name
                    }));
                }
            }
        });
        GetAtsAgreementMastersHelper("replacement_period").then((res) => {
            if (res && !res.hasError && res.data?.data) {
                setReplacementPeriods(res.data.data);
                if (res.data.data.length > 0) {
                    const item = res.data.data[0];
                    setForm(prev => ({
                        ...prev,
                        replacement_months: item.value_months || item.value
                    }));
                }
            }
        });
        GetAtsAgreementMastersHelper("agreement_duration").then((res) => {
            if (res && !res.hasError && res.data?.data) {
                setAgreementDurations(res.data.data);
                if (res.data.data.length > 0) {
                    const item = res.data.data[0];
                    setForm(prev => ({
                        ...prev,
                        period_months: String(item.value_months || item.value)
                    }));
                }
            }
        });
    }, [dispatch, isGroupOrg]);

    useEffect(() => {
        dispatch(GetAtsAgreementsAction({}));
    }, [dispatch, inactive]);

    useEffect(() => {
        if (!isAdmin) return;
        const fetchRequests = async () => {
            const res = await GetFeeOverrideRequestsHelper();
            if (!res.hasError) setApprovalRequests(res.data?.data || []);
        };
        fetchRequests();
    }, [isAdmin]);

    const handleCheckRequestStatus = async () => {
        if (!overrideRequestId) return;
        const res = await GetFeeOverrideRequestsHelper({ request_id: overrideRequestId });
        if (!res.hasError && res.data?.data) {
            const data = res.data.data;
            const req = Array.isArray(data) ? data[0] : data;
            setActiveRequest(req || null);
        }
    };
    useEffect(() => {
        const shouldPoll = (overrideRequestId && overrideStatus === "pending") || (isAdmin && adminApprovalOpen);
        if (!shouldPoll) return;

        const interval = setInterval(() => {
            if (isAdmin && adminApprovalOpen) {
                GetFeeOverrideRequestsHelper().then(res => {
                    if (!res.hasError) setApprovalRequests(res.data?.data || []);
                });
            }
            if (overrideRequestId && overrideStatus === "pending") {
                handleCheckRequestStatus();
            }
        }, 15000);
        return () => clearInterval(interval);
    }, [isAdmin, adminApprovalOpen, overrideRequestId, overrideStatus]);

    useEffect(() => {
        if (generateRow) {
            GetAtsAgreementTemplatesHelper().then((res) => {
                if (res && !res.hasError && res.data?.data) {
                    setTemplatesList(res.data.data);
                    setAgreementTypes(res.data.data.map(t => t.agreement_type));
                }
            });
            GetReportingManagersHelper().then((res) => {
                const list = res.hasError ? [] : (res.data?.data || []);
                const unique = [];
                const seen = new Set();
                for (const u of list) {
                    const name = (u.name || u.full_name || u.employee_name || "").trim();
                    if (name && !seen.has(name)) {
                        seen.add(name);
                        unique.push({
                            name: name,
                            title: u.designation || u.designation_name || u.title || u.role || u.role_name || ""
                        });
                    }
                }
                setSignatories(unique);
            }).catch(() => {});
        }
    }, [generateRow]);

    useEffect(() => {
        if (!generateRow?.client_id) {
            setActiveRequest(null);
            return;
        }
        GetFeeOverrideRequestsHelper({ client_id: generateRow.client_id }).then(res => {
            if (!res.hasError && res.data?.data) {
                const data = res.data.data;
                const req = Array.isArray(data)
                    ? data.find(r => String(r.client_id) === String(generateRow.client_id))
                    : data;
                setActiveRequest(req || null);
            } else {
                setActiveRequest(null);
            }
        });
    }, [generateRow, isAdmin]);

    useEffect(() => {
        const effectiveFee = form.commission_pct === "custom" ? form.custom_fee : form.commission_pct;
        const matches = activeRequest &&
            Math.abs(parseFloat(activeRequest.commission_pct) - parseFloat(effectiveFee)) < 0.01;

        if (matches) {
            setOverrideRequestId(activeRequest.id);
            setOverrideStatus(activeRequest.status);
            if (activeRequest.status === "approved") {
                setForm(f => ({ ...f, admin_override: true }));
                setAdminWarning(false);
            }
        } else {
            setOverrideRequestId(null);
            setOverrideStatus(null);
        }
    }, [activeRequest, form.commission_pct, form.custom_fee]);


    const agreements = atsAgreementData || [];

    // ── KPIs ─────────────────────────────────────────────────────────────────
    const active = agreements.filter(a => a.status === "Active").length;
    const expiring = agreements.filter(a => {
        if (!a.end_date || a.status !== "Active") return false;
        const diff = (new Date(a.end_date) - new Date()) / (1000 * 60 * 60 * 24);
        return diff >= 0 && diff <= 30;
    }).length;
    const totalValue = agreements.reduce((s, a) => s + (Number(a.value) || 0), 0);
    const uniqueClients = new Set(agreements.map(a => a.client_name || a.client_id)).size;

    // ── Filter ────────────────────────────────────────────────────────────────
    const filtered = React.useMemo(() => {
        return agreements.filter(a => {
            const clientMatch = !filterClient || a.client_name === filterClient?.name;
            const statusMatch = filterStatus === "all" || a.status === filterStatus;
            const toggleMatch = inactive ? a.status === "Expired" : a.status !== "Expired";
            return clientMatch && statusMatch && toggleMatch;
        });
    }, [agreements, filterClient, filterStatus, isGroupOrg, inactive]);

    // ── Dialog handlers ───────────────────────────────────────────────────────
    const handleOpen = () => {
        setForm(EMPTY);
        setFormErrors({});
        setEditMode(false);
        setEditId(null);
        setActiveStep(0);
        setOpenDialog(true);
    };

    const handleEdit = (row) => {
        let templateData = {};
        if (row.template_responses) {
            try {
                templateData = typeof row.template_responses === 'string' ? JSON.parse(row.template_responses) : row.template_responses;
            } catch(e) {}
        }
        const rowFee = row.commission_pct ? String(parseFloat(row.commission_pct)) : "";
        const isStandard = rowFee && commissionOptions.some(o => String(o.value_pct || o.value) === rowFee);

        setForm({
            client_id: isGroupOrg
                ? atsGroupCompanyData?.find(c => c.name === row.client_name) || ""
                : atsCompanyData?.find(c => c.name === row.client_name) || "",
            agreement_type: row.agreement_type || "",
            start_date: row.start_date || "",
            end_date: row.end_date || "",
            value: row.value || "",
            commission_pct: isStandard ? rowFee : (rowFee ? "custom" : ""),
            custom_fee: isStandard ? "" : rowFee,
            notes: row.notes || "",
            status: row.status || "Active",
            authority_name: templateData.authority_name || "",
            authority_title: templateData.authority_title || "",
            period_months: templateData.period_months || "",
            replacement_months: templateData.replacement_months || "",
            admin_override: templateData.admin_override || false,
            ...templateData,
        });
        setFormErrors({});
        setEditId(row.agreement_id);
        setEditMode(true);
        setActiveStep(0);
        setOpenDialog(true);
    };

    const handleSave = () => {
        if (!validateStep2()) return;
        const effectiveFee = form.commission_pct === "custom" ? form.custom_fee : form.commission_pct;
        const nonStd = isNonStandardFee(effectiveFee);

        if (nonStd && !form.admin_override) {
            setAdminWarning(true);
            return;
        }

        const clientId = isGroupOrg
            ? form.client_id?.ats_grouporg_id || form.client_id
            : form.client_id?.ats_company_id || form.client_id;
        const clientName = form.client_id?.name || "";

        let calculatedEndDate = form.end_date;
        if (form.start_date && form.period_months) {
            const sd = new Date(form.start_date);
            sd.setMonth(sd.getMonth() + parseInt(form.period_months));
            calculatedEndDate = dateStr(sd);
        }

        let calculatedStatus = form.status;
        if (calculatedEndDate) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const endDateObj = new Date(calculatedEndDate);
            endDateObj.setHours(0, 0, 0, 0);

            if (endDateObj >= today && calculatedStatus === "Expired") {
                calculatedStatus = "Active";
            } else if (endDateObj < today && calculatedStatus === "Active") {
                calculatedStatus = "Expired";
            }
        }

        const standardNames = [
            "client_id", "client_name", "agreement_type", "start_date", "end_date", "value", "commission_pct", "notes", "status", "admin_override"
        ];
        const templateResponses = {};
        Object.keys(form).forEach(key => {
            if (!standardNames.includes(key)) {
                templateResponses[key] = form[key];
            }
        });

        const payload = {
            client_id: clientId,
            client_name: clientName,
            agreement_type: form.agreement_type,
            start_date: form.start_date,
            end_date: calculatedEndDate || null,
            value: form.value || null,
            commission_pct: effectiveFee,
            notes: form.notes || null,
            status: calculatedStatus,
            template_responses: templateResponses,
        };
        if (editMode) {
            dispatch(UpdateAtsAgreementAction({ ...payload, agreement_id: editId }));
        } else {
            dispatch(AddAtsAgreementAction(payload));
        }
        setOpenDialog(false);
        setActiveStep(0);
        setAdminWarning(false);
        setOverrideRequestId(null);
        setOverrideStatus(null);
    };

    const handleDelete = () => {
        dispatch(DeleteAtsAgreementAction({ agreement_id: deleteConfirm.agreement_id }));
        setDeleteConfirm(null);
    };

    const isNonStandardFee = (v) => !commissionOptions.map(c => String(c.value_pct || c.value)).includes(String(v));

    const handleGenerateDraft = async (row) => {
        const companyName = sessionStorage.getItem("company_full_name") || getUserData()?.company_full_name;

        let templateData = {};
        if (row.template_responses) {
            try {
                templateData = typeof row.template_responses === 'string' ? JSON.parse(row.template_responses) : row.template_responses;
            } catch(e) {}
        }

        await GenerateAgreementDocHelper({
            client_id: row.client_id,
            client_name: row.client_name,
            notes: row.notes,
            org_name: companyName,
            ...templateData,
            agreement_type: row.agreement_type,
            commission_pct: row.commission_pct,
            start_date: row.start_date,
            admin_override: true,
        });
    };

    // Employee sends an approval request to admins
    const handleRequestOverride = async () => {
        const effectiveFee = form.commission_pct === "custom" ? form.custom_fee : form.commission_pct;
        const clientId = isGroupOrg
            ? form.client_id?.ats_grouporg_id || form.client_id
            : form.client_id?.ats_company_id || form.client_id;

        setOverrideSending(true);
        const companyName = sessionStorage.getItem("company_full_name") || getUserData()?.company_full_name;
        const res = await RequestFeeOverrideHelper({
            client_id: clientId,
            client_name: form.client_id?.name || "",
            commission_pct: effectiveFee,
            start_date: form.start_date,
            replacement_months: form.replacement_months,
            notes: form.notes,
            authority_name: form.authority_name,
            authority_title: form.authority_title,
            period_months: form.period_months,
            org_name: companyName,
        });
        setOverrideSending(false);

        if (!res.hasError) {
            const reqId = res.data?.data?.request_id;
            const newReq = {
                id: reqId,
                client_id: clientId,
                commission_pct: effectiveFee,
                fee_basis: form.fee_basis,
                status: "pending"
            };
            setActiveRequest(newReq);
            if (clientId && reqId) {
                sessionStorage.setItem(`override_req_${clientId}`, reqId);
            }
        }
    };

    // Admin approves or denies a pending request
    const handleRespondOverride = async (request_id, action) => {
        const res = await RespondFeeOverrideHelper({ request_id, action });
        if (!res.hasError) {
            setApprovalRequests(prev => prev.filter(r => r.id !== request_id));
        }
    };


    // ── Signed copy handlers ─────────────────────────────────────────────────
    const handleOpenSignedCopy = (row) => {
        setSignedCopyRow(row);
        setSignedFile(null);
    };

    const handleSignedFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type !== "application/pdf") {
            return;
        }
        setSignedFile(file);
    };

    const handleUploadSignedCopy = async () => {
        if (!signedFile || !signedCopyRow) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            const b64 = e.target.result; // full data URL
            dispatch(UploadAgreementSignedCopyAction({
                agreement_id: signedCopyRow.agreement_id,
                file_b64: b64,
                file_name: signedFile.name,
            }));
            setSignedCopyRow(null);
            setSignedFile(null);
        };
        reader.readAsDataURL(signedFile);
    };

    const handleViewSignedCopy = async () => {
        if (!signedCopyRow?.signed_copy_path) return;
        setSignedUrlLoading(true);
        const res = await GetAgreementSignedUrlHelper({ signed_copy_path: signedCopyRow.signed_copy_path });
        setSignedUrlLoading(false);
        if (!res.hasError && res?.data?.data?.url) {
            window.open(res.data.data.url, "_blank");
        }
    };

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns = React.useMemo(() => [
        {
            title: "Agreement ID", field: "agreement_id", width: 120,
            cellStyle: { padding: "10px 10px 10px 13px", color: "#94A3B8", fontSize: "0.75rem" },
            headerStyle: { padding: "10px 10px 10px 13px" },
        },
        {
            title: isGroupOrg ? "Group Org" : "Client", field: "client_name",
            cellStyle: { padding: "10px", fontWeight: 600 }, headerStyle: { padding: "10px" },
        },
        {
            title: "Type", field: "agreement_type",
            render: r => <Chip label={r.agreement_type || "—"} size="small" sx={{ bgcolor: "#F3EDFD", color: BRAND, fontWeight: 600, fontSize: "0.68rem" }} />,
            cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" },
        },
        {
            title: "Start Date", field: "start_date",
            render: r => r.start_date ? ConvertDate(r.start_date) : "—",
            cellStyle: { padding: "10px", whiteSpace: "nowrap" }, headerStyle: { padding: "10px" },
        },
        {
            title: "End Date", field: "end_date",
            render: r => {
                if (!r.end_date) return <Typography fontSize="0.78rem" color="text.secondary">Open-ended</Typography>;
                const diff = Math.round((new Date(r.end_date) - new Date()) / (1000 * 60 * 60 * 24));
                const color = diff < 0 ? "#DC2626" : diff <= 30 ? "#D97706" : "#059669";
                return (
                    <Box>
                        <Typography fontSize="0.78rem" color="inherit">{ConvertDate(r.end_date)}</Typography>
                        {diff >= 0 && <Typography fontSize="0.65rem" color={color}>{diff}d remaining</Typography>}
                        {diff < 0 && <Typography fontSize="0.65rem" color={color}>Expired</Typography>}
                    </Box>
                );
            },
            cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" },
        },

        {
            title: "Professional Fee", field: "commission_pct", type: "numeric",
            render: r => r.commission_pct ? <Chip label={`${r.commission_pct}%`} size="small" sx={{ bgcolor: "#DBEAFE", color: "#2563EB", fontWeight: 700, fontSize: "0.68rem" }} /> : "—",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
        },
        {
            title: "Status", field: "status",
            render: r => {
                const s = STATUS_STYLES[r.status] || { bg: "#F1F5F9", color: "#64748B" };
                return <Chip label={r.status || "—"} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: "0.68rem" }} />;
            },
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
        },
        {
            title: "Signed Copy", field: "signed_copy_path",
            render: r => r.signed_copy_path
                ? <Chip icon={<CheckCircleIcon sx={{ fontSize: "0.85rem !important", color: "#059669 !important" }} />} label="Attached" size="small" sx={{ bgcolor: "#D1FAE5", color: "#059669", fontWeight: 700, fontSize: "0.68rem" }} />
                : <Chip label="Pending" size="small" sx={{ bgcolor: "#FEE2E2", color: "#DC2626", fontWeight: 700, fontSize: "0.68rem" }} />,
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
        },
        {
            title: "Notes", field: "notes",
            render: r => r.notes
                ? <Tooltip title={r.notes}><Typography fontSize="0.78rem" noWrap sx={{ maxWidth: 150 }}>{r.notes}</Typography></Tooltip>
                : "—",
            cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" },
        },
    ], [isGroupOrg]);

    return (
        <Base>
            <Box sx={{ maxHeight: "87vh", overflowY: "auto", px: 0.5 }}>
                {/* ── Header ─────────────────────────────────────────────── */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <HandshakeOutlinedIcon sx={{ color: BRAND, fontSize: 22 }} />
                        <Typography variant="h6" fontWeight={700} fontSize="1rem">Agreements</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {isAdmin && (
                            <Tooltip title={approvalRequests.length > 0 ? `${approvalRequests.length} pending fee override request(s)` : "No pending fee override requests"}>
                                <IconButton
                                    size="small"
                                    onClick={async () => {
                                        setAdminApprovalOpen(true);
                                        const res = await GetFeeOverrideRequestsHelper();
                                        if (!res.hasError) setApprovalRequests(res.data?.data || []);
                                    }}
                                    sx={{ bgcolor: approvalRequests.length > 0 ? "#FEF3C7" : "transparent", border: approvalRequests.length > 0 ? "1.5px solid #D97706" : "none", borderRadius: 2 }}
                                >
                                    <Badge badgeContent={approvalRequests.length} color="warning" max={99}>
                                        <NotificationsActiveIcon fontSize="small" sx={{ color: approvalRequests.length > 0 ? "#D97706" : "text.secondary" }} />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                        )}
                        <Button
                            variant="contained" startIcon={<AddIcon />} size="small"
                            className="mpl-primary-btn"
                            sx={{ textTransform: "none", fontWeight: 600 }}
                            onClick={handleOpen}
                        >
                            New Agreement
                        </Button>
                    </Box>
                </Box>

                {/* ── KPIs ────────────────────────────────────────────────── */}
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={4} md={4} lg={4}>
                        <KpiCard icon={<HandshakeOutlinedIcon />} value={agreements.length} label="Total Agreements" color="#4E79A7" />
                    </Grid>
                    <Grid item xs={4} md={4} lg={4}>
                        <KpiCard icon={<CheckCircleOutlineIcon />} value={active} label="Active" color="#10B981" />
                    </Grid>
                    <Grid item xs={4} md={4} lg={4}>
                        <KpiCard icon={<AccessTimeOutlinedIcon />} value={expiring} label="Expiring in 30d" color={expiring > 0 ? "#D97706" : "#10B981"} />
                    </Grid>
                </Grid>

                {/* ── Filters ─────────────────────────────────────────────── */}
                <Grid container columnSpacing={1.5} rowSpacing={1} mb={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        {isGroupOrg ? (
                            <SelectableSearch
                                isOptionEqualToValue={(o, v) => +o.ats_grouporg_id === +v.ats_grouporg_id}
                                label="Group Organisation" value={filterClient}
                                loading={() => setCompanyData({ load: false, values: atsGroupCompanyData?.filter(c => c.status === "Active") || [] })}
                                fieldLabel="name" required={false} variant="outlined"
                                name="filter_client" size="small"
                                onChangeEvent={(e, v) => setFilterClient(v || "")}
                                data={companyData}
                            />
                        ) : (
                            <SelectableSearch
                                isOptionEqualToValue={(o, v) => +o.ats_company_id === +v.ats_company_id}
                                label="Client" value={filterClient}
                                loading={() => setCompanyData({ load: false, values: (atsCompanyData?.filter(c => c.status === "Active") || []).sort((a, b) => (a.name || "").localeCompare(b.name || "")) })}
                                fieldLabel="name" required={false} variant="outlined"
                                name="filter_client" size="small"
                                onChangeEvent={(e, v) => setFilterClient(v || "")}
                                data={companyData}
                            />
                        )}
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                                <MenuItem value="all">All</MenuItem>
                                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={4} md={5}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, ml: { sm: 1, xs: 0 }, mt: { xs: 1, sm: 0 } }}>
                            {Object.entries(STATUS_STYLES).map(([status, style]) => {
                                const count = agreements.filter(a => a.status === status).length;
                                return count > 0 ? (
                                    <Box
                                        key={status}
                                        onClick={() => setFilterStatus(filterStatus === status ? "all" : status)}
                                        sx={{ display: "flex", alignItems: "center", gap: 0.8, px: 1.5, py: 0.6, borderRadius: 3, cursor: "pointer", border: "1.5px solid", borderColor: filterStatus === status ? style.color : "transparent", bgcolor: style.bg, transition: "all 0.2s", "&:hover": { opacity: 0.8 } }}
                                    >
                                        <Typography fontSize="0.72rem" fontWeight={700} color={style.color}>{status}</Typography>
                                        <Avatar sx={{ width: 20, height: 20, bgcolor: style.color, fontSize: "0.65rem", fontWeight: 800 }}>{count}</Avatar>
                                    </Box>
                                ) : null;
                            })}
                        </Box>
                    </Grid>
                </Grid>

                {/* ── Table ───────────────────────────────────────────────── */}
                <MaterialTable
                    columns={columns}
                    data={filtered}
                    isLoading={isLoading}
                    actions={[
                        {
                            icon: () => <DescriptionOutlinedIcon fontSize="small" sx={{ color: "#10B981" }} />,
                            tooltip: "Generate Agreement Draft (.docx)",
                            onClick: (_, row) => handleGenerateDraft(row),
                        },
                        {
                            // Dynamic green/red signed copy button
                            icon: (row) => (
                                <AttachFileIcon
                                    fontSize="small"
                                    sx={{ color: row?.signed_copy_path ? "#10B981" : "#EF4444" }}
                                />
                            ),
                            tooltip: "Upload / View Signed Copy",
                            onClick: (_, row) => handleOpenSignedCopy(row),
                        },
                        {
                            icon: () => <EditOutlinedIcon fontSize="small" sx={{ color: BRAND }} />,
                            tooltip: "Edit Agreement",
                            onClick: (_, row) => handleEdit(row),
                        },
                        {
                            icon: () => <DeleteOutlineIcon fontSize="small" sx={{ color: "#EF4444" }} />,
                            tooltip: "Delete Agreement",
                            onClick: (_, row) => setDeleteConfirm(row),
                        },
                    ]}
                    options={{
                        maxBodyHeight: "48vh",
                        minBodyHeight: "200px",
                        sorting: true,
                        search: true,
                        searchFieldAlignment: "right",
                        searchAutoFocus: true,
                        searchFieldVariant: "standard",
                        filtering: false,
                        exportButton: true,
                        exportAllData: true,
                        paging: true,
                        pageSizeOptions: [10, 25, 50],
                        pageSize: 25,
                        actionsColumnIndex: -1,
                        draggable: false,
                        columnsButton: true,
                        rowStyle: { fontSize: "13px" },
                        headerStyle: {
                            backgroundColor:
                                theme?.palette?.mode === "light"
                                    ? theme.palette.grey[0]
                                    : theme.palette.grey[500],
                            color:
                                theme?.palette?.mode === "light"
                                    ? theme.palette.primary.contrastText
                                    : theme.palette.grey[800],
                            fontWeight: "bold",
                            padding: "5px 10px 5px 13px",
                            fontSize: "13px",
                        },
                    }}
                    title="Agreements"
                />
                <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setActiveStep(0); setAdminWarning(false); }} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
                        <HandshakeOutlinedIcon sx={{ color: BRAND, fontSize: 20 }} />
                        {editMode ? "Edit Agreement" : "New Agreement"} - Step {activeStep + 1} of 2
                    </DialogTitle>
                    <DialogContent dividers>
                        {activeStep === 0 && (
                            <Grid container spacing={1.5} sx={{ mt: 0 }}>
                                <Grid item xs={12} sm={6}>
                                    {isGroupOrg ? (
                                        <SelectableSearch
                                            isOptionEqualToValue={(o, v) => +o.ats_grouporg_id === +v.ats_grouporg_id}
                                            label="Group Organisation" value={form.client_id}
                                            loading={() => setCompanyData({ load: false, values: (atsGroupCompanyData?.filter(c => c.status === "Active") || []).sort((a, b) => (a.name || "").localeCompare(b.name || "")) })}
                                            fieldLabel="name" required variant="outlined" name="client_id" size="small"
                                            onChangeEvent={(e, v) => handleFormChange("client_id", v || "")}
                                            data={companyData}
                                            error={!!formErrors.client_id}
                                            helperText={formErrors.client_id}
                                        />
                                    ) : (
                                        <SelectableSearch
                                            isOptionEqualToValue={(o, v) => +o.ats_company_id === +v.ats_company_id}
                                            label="Client" value={form.client_id}
                                            loading={() => setCompanyData({ load: false, values: (atsCompanyData?.filter(c => c.status === "Active") || []).sort((a, b) => (a.name || "").localeCompare(b.name || "")) })}
                                            fieldLabel="name" required variant="outlined" name="client_id" size="small"
                                            onChangeEvent={(e, v) => handleFormChange("client_id", v || "")}
                                            data={companyData}
                                            error={!!formErrors.client_id}
                                            helperText={formErrors.client_id}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl size="small" fullWidth required error={!!formErrors.agreement_type}>
                                        <InputLabel>Agreement Type / Template</InputLabel>
                                        <Select value={form.agreement_type} label="Agreement Type / Template" onChange={e => handleFormChange("agreement_type", e.target.value)}>
                                            {agreementTypes.length === 0 ? (
                                                <MenuItem disabled value="">No templates configured. Please configure in settings first.</MenuItem>
                                            ) : (
                                                agreementTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)
                                            )}
                                        </Select>
                                        {formErrors.agreement_type && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{formErrors.agreement_type}</Typography>}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select value={form.status} label="Status" onChange={e => handleFormChange("status", e.target.value)}>
                                            {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CommonDatePicker
                                        required
                                        error={!!formErrors.start_date}
                                        helperText={formErrors.start_date}
                                        size="small" name="start_date" label="Start Date" pickerType="date"
                                        onKeyDown={e => e.preventDefault()}
                                        pickerFormat="dd/MM/yyyy" DefaultDTvalue={form.start_date}
                                        handleChange={v => handleFormChange("start_date", v ? dateStr(v) : "")}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl size="small" fullWidth required error={!!formErrors.period_months}>
                                        <InputLabel>Agreement Duration</InputLabel>
                                        <Select
                                            value={form.period_months}
                                            label="Agreement Duration"
                                            onChange={e => handleFormChange("period_months", e.target.value)}
                                        >
                                            {agreementDurations.length === 0 ? (
                                                <MenuItem disabled value="">No durations configured.</MenuItem>
                                            ) : (
                                                agreementDurations.map(d => (
                                                    <MenuItem key={d.id} value={String(d.value_months || d.value)}>
                                                        {d.name || `${d.value_months} Months`}
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                        {formErrors.period_months && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{formErrors.period_months}</Typography>}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField size="small" fullWidth label="Contract Value (Optional)"
                                        value={form.value} type="number"
                                        onChange={e => handleFormChange("value", e.target.value)}
                                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField size="small" fullWidth multiline rows={2} label="Notes"
                                        value={form.notes}
                                        onChange={e => handleFormChange("notes", e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {activeStep === 1 && (
                            <Grid container spacing={1.5} sx={{ mt: 0 }}>
                                {/* ── Admin override warning banner ──────────────────── */}
                                {adminWarning && (
                                    <Grid item xs={12}>
                                        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: "#FEF3C7", border: "1.5px solid #D97706", display: "flex", flexDirection: "column", gap: 0.75 }}>
                                            <Typography fontSize="0.82rem" fontWeight={700} color="#92400E">
                                                ⚠ Non-Standard Fee — Admin Approval Required
                                            </Typography>
                                            <Typography fontSize="0.75rem" color="#92400E">
                                                The fee of <strong>{form.commission_pct === "custom" ? form.custom_fee : form.commission_pct}%</strong> is outside the standard range ({commissionOptions.map(c => String(c.value_pct || c.value) + "%").join(", ")}).
                                            </Typography>

                                            {/* Admin/Owner can self-approve */}
                                            {isAdmin && (
                                                <Button
                                                    size="small" variant="contained"
                                                    sx={{ alignSelf: "flex-start", mt: 0.5, textTransform: "none", fontWeight: 600, bgcolor: "#D97706", "&:hover": { bgcolor: "#B45309" } }}
                                                    onClick={() => { handleFormChange("admin_override", true); setAdminWarning(false); }}
                                                >
                                                    Confirm Override &amp; Proceed
                                                </Button>
                                            )}

                                            {/* Employee: Request → Awaiting → Approved flow */}
                                            {!isAdmin && overrideStatus === null && (
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                    <Typography fontSize="0.75rem" color="#92400E">
                                                        Click below to notify your Admin / Owner and request approval.
                                                    </Typography>
                                                    <Button
                                                        size="small" variant="contained"
                                                        disabled={overrideSending}
                                                        sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 600, bgcolor: "#D97706", "&:hover": { bgcolor: "#B45309" } }}
                                                        onClick={handleRequestOverride}
                                                        startIcon={overrideSending ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <NotificationsActiveIcon fontSize="small" />}
                                                    >
                                                        {overrideSending ? "Sending..." : "Request Admin Approval"}
                                                    </Button>
                                                </Box>
                                            )}
                                            {!isAdmin && overrideStatus === "pending" && (
                                                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mt: 0.5 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <CircularProgress size={16} sx={{ color: "#D97706" }} />
                                                        <Typography fontSize="0.75rem" color="#92400E" fontWeight={600}>
                                                            Approval request sent. Waiting for Admin / Owner to approve…
                                                        </Typography>
                                                    </Box>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={handleCheckRequestStatus}
                                                        sx={{ textTransform: "none", py: 0.25, fontSize: "0.7rem", color: "#D97706", borderColor: "#D97706", "&:hover": { borderColor: "#B45309", bgcolor: "rgba(217, 119, 6, 0.04)" } }}
                                                    >
                                                        Check Status
                                                    </Button>
                                                </Box>
                                            )}
                                            {!isAdmin && overrideStatus === "denied" && (
                                                <Typography fontSize="0.75rem" color="#B91C1C" fontWeight={600}>
                                                    ✕ Your request was denied. Please contact your Admin / Owner directly.
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                )}

                                {/* Professional Fee % */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl size="small" fullWidth required error={!!formErrors.commission_pct}>
                                        <InputLabel>Professional Fee %</InputLabel>
                                        <Select
                                            value={form.commission_pct}
                                            label="Professional Fee %"
                                            onChange={e => handleFormChange("commission_pct", e.target.value)}
                                        >
                                            {commissionOptions.length === 0 ? (
                                                <MenuItem disabled value="">No fee options configured. Please configure in settings first.</MenuItem>
                                            ) : (
                                                commissionOptions.map(o => (
                                                    <MenuItem key={o.id} value={String(o.value_pct || o.value)}>
                                                        {o.name || `${o.value_pct}%`}
                                                    </MenuItem>
                                                ))
                                            )}
                                            <MenuItem value="custom">Custom / Exemption</MenuItem>
                                        </Select>
                                        {formErrors.commission_pct && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{formErrors.commission_pct}</Typography>}
                                    </FormControl>
                                </Grid>

                                {/* Fee Basis */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl size="small" fullWidth required error={!!formErrors.fee_basis}>
                                        <InputLabel>Fee Basis</InputLabel>
                                        <Select
                                            value={form.fee_basis || ""}
                                            label="Fee Basis"
                                            onChange={e => handleFormChange("fee_basis", e.target.value)}
                                        >
                                            {feeBasisOptions.length === 0 ? (
                                                <MenuItem disabled value="">No fee basis configured.</MenuItem>
                                            ) : (
                                                feeBasisOptions.map(opt => (
                                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                ))
                                            )}
                                        </Select>
                                        {formErrors.fee_basis && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{formErrors.fee_basis}</Typography>}
                                    </FormControl>
                                </Grid>

                                {/* Custom fee field */}
                                {form.commission_pct === "custom" && (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            size="small" fullWidth required
                                            error={!!formErrors.custom_fee}
                                            label="Custom Fee %"
                                            value={form.custom_fee}
                                            type="number"
                                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                                            onChange={e => handleFormChange("custom_fee", e.target.value)}
                                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                            helperText={formErrors.custom_fee || "Needs admin approval"}
                                            FormHelperTextProps={{ sx: { color: formErrors.custom_fee ? "#d32f2f" : "#D97706" } }}
                                        />
                                    </Grid>
                                )}

                                {/* Replacement Duration */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl size="small" fullWidth required error={!!formErrors.replacement_months}>
                                        <InputLabel>Replacement Duration</InputLabel>
                                        <Select
                                            value={form.replacement_months}
                                            label="Replacement Duration"
                                            onChange={e => handleFormChange("replacement_months", e.target.value)}
                                        >
                                            {replacementPeriods.length === 0 ? (
                                                <MenuItem disabled value="">No replacement periods configured.</MenuItem>
                                            ) : (
                                                replacementPeriods.map(r => (
                                                    <MenuItem key={r.id} value={String(r.value_months || r.value)}>
                                                        {r.name || `${r.value_months} Months`}
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                        {formErrors.replacement_months && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{formErrors.replacement_months}</Typography>}
                                    </FormControl>
                                </Grid>

                                {/* Signing Authority Name */}
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        freeSolo
                                        forcePopupIcon={true}
                                        size="small"
                                        fullWidth
                                        options={signatories.map(s => s.name)}
                                        value={form.authority_name || ""}
                                        onChange={(event, newValue) => {
                                            if (typeof newValue === 'string') {
                                                handleFormChange("authority_name", newValue);
                                                const selected = signatories.find(s => s.name === newValue);
                                                if (selected) handleFormChange("authority_title", selected.title);
                                            } else if (newValue && newValue.inputValue) {
                                                handleFormChange("authority_name", newValue.inputValue);
                                            } else {
                                                handleFormChange("authority_name", "");
                                            }
                                        }}
                                        filterOptions={(options, params) => {
                                            const filtered = filter(options, params);
                                            const { inputValue } = params;
                                            const isExisting = options.some((option) => inputValue === option);
                                            if (inputValue !== '' && !isExisting) {
                                                filtered.push({
                                                    inputValue,
                                                    title: `Add "${inputValue}"`,
                                                });
                                            }
                                            return filtered;
                                        }}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') return option;
                                            if (option.inputValue) return option.inputValue;
                                            return option.title || "";
                                        }}
                                        renderOption={(props, option) => {
                                            if (option && option.inputValue) {
                                                return (
                                                    <li {...props} style={{ cursor: 'pointer' }}>
                                                        <span style={{ display: "flex", alignItems: "center", color: "#883bd5", fontWeight: 600, marginRight: 4 }}>
                                                            <AddIcon sx={{ fontSize: 18 }} /> Add
                                                        </span>
                                                        <span>"{option.inputValue}"</span>
                                                    </li>
                                                );
                                            }
                                            return <li {...props}>{option}</li>;
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                label="Signing Authority Name"
                                                error={!!formErrors.authority_name}
                                                helperText={formErrors.authority_name}
                                                placeholder="Select or type signing authority name"
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Signing Authority Designation */}
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        freeSolo
                                        forcePopupIcon={true}
                                        size="small"
                                        fullWidth
                                        options={[...new Set(signatories.map(s => s.title))].filter(Boolean)}
                                        value={form.authority_title || ""}
                                        onChange={(event, newValue) => {
                                            if (typeof newValue === 'string') {
                                                handleFormChange("authority_title", newValue);
                                            } else if (newValue && newValue.inputValue) {
                                                handleFormChange("authority_title", newValue.inputValue);
                                            } else {
                                                handleFormChange("authority_title", "");
                                            }
                                        }}
                                        filterOptions={(options, params) => {
                                            const filtered = filter(options, params);
                                            const { inputValue } = params;
                                            const isExisting = options.some((option) => inputValue === option);
                                            if (inputValue !== '' && !isExisting) {
                                                filtered.push({
                                                    inputValue,
                                                    title: `Add "${inputValue}"`,
                                                });
                                            }
                                            return filtered;
                                        }}
                                        getOptionLabel={(option) => {
                                            if (typeof option === 'string') return option;
                                            if (option.inputValue) return option.inputValue;
                                            return option.title || "";
                                        }}
                                        renderOption={(props, option) => {
                                            if (option && option.inputValue) {
                                                return (
                                                    <li {...props} style={{ cursor: 'pointer' }}>
                                                        <span style={{ display: "flex", alignItems: "center", color: "#883bd5", fontWeight: 600, marginRight: 4 }}>
                                                            <AddIcon sx={{ fontSize: 18 }} /> Add
                                                        </span>
                                                        <span>"{option.inputValue}"</span>
                                                    </li>
                                                );
                                            }
                                            return <li {...props}>{option}</li>;
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Designation / Title"
                                                placeholder="Select or type e.g. Business Head"
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Dynamic Custom Template Prompts */}
                                {renderDynamicTemplatePrompts()}
                            </Grid>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button onClick={() => { setOpenDialog(false); setActiveStep(0); setAdminWarning(false); }} variant="outlined" size="small" sx={{ textTransform: "none" }}>Cancel</Button>
                        {activeStep === 0 && (
                            <Button
                                onClick={() => {
                                    if (validateStep1()) {
                                        setActiveStep(1);
                                    }
                                }}
                                variant="contained" size="small"
                                className="mpl-primary-btn"
                                sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                                Next
                            </Button>
                        )}
                        {activeStep === 1 && (
                            <>
                                <Button onClick={() => setActiveStep(0)} variant="outlined" size="small" sx={{ textTransform: "none" }}>Back</Button>
                                <Button
                                    onClick={handleSave}
                                    variant="contained" size="small"
                                    disabled={(!isAdmin && adminWarning)}
                                    className="mpl-primary-btn"
                                    sx={{ textTransform: "none", fontWeight: 600 }}
                                >
                                    {editMode ? "Save Changes" : "Create & Save"}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>

                <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: "0.95rem" }}>Delete Agreement</DialogTitle>
                    <DialogContent>
                        <Typography fontSize="0.9rem">
                            Are you sure you want to delete the agreement with <strong>{deleteConfirm?.client_name}</strong>? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button onClick={() => setDeleteConfirm(null)} variant="outlined" size="small" sx={{ textTransform: "none" }}>Cancel</Button>
                        <Button onClick={handleDelete} variant="contained" color="error" size="small" sx={{ textTransform: "none", fontWeight: 600 }}>Delete</Button>
                    </DialogActions>
                </Dialog>



                {/* ── Signed Copy Upload / View Dialog ─────────────────────── */}
                <Dialog open={!!signedCopyRow} onClose={() => { setSignedCopyRow(null); setSignedFile(null); }} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
                        <AttachFileIcon sx={{ color: signedCopyRow?.signed_copy_path ? "#10B981" : "#EF4444", fontSize: 20 }} />
                        Signed Agreement Copy
                        <Chip
                            label={signedCopyRow?.signed_copy_path ? "Attached" : "Not Attached"}
                            size="small"
                            sx={{
                                ml: "auto",
                                bgcolor: signedCopyRow?.signed_copy_path ? "#D1FAE5" : "#FEE2E2",
                                color: signedCopyRow?.signed_copy_path ? "#059669" : "#DC2626",
                                fontWeight: 700,
                                fontSize: "0.65rem",
                            }}
                        />
                    </DialogTitle>
                    <DialogContent dividers>
                        <Typography fontSize="0.82rem" color="text.secondary" mb={2}>
                            <strong>{signedCopyRow?.client_name}</strong> — {signedCopyRow?.agreement_type}
                        </Typography>

                        {/* View existing copy */}
                        {signedCopyRow?.signed_copy_path && (
                            <Box
                                sx={{
                                    mb: 2, p: 1.5, borderRadius: 2,
                                    border: "1.5px solid #D1FAE5", bgcolor: "#F0FDF4",
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: "#059669", fontSize: 18 }} />
                                    <Typography fontSize="0.78rem" fontWeight={600} color="#059669">
                                        Signed copy available
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={signedUrlLoading ? <CircularProgress size={12} /> : <VisibilityOutlinedIcon />}
                                    disabled={signedUrlLoading}
                                    onClick={handleViewSignedCopy}
                                    sx={{ textTransform: "none", fontSize: "0.72rem", borderColor: "#059669", color: "#059669", "&:hover": { bgcolor: "#D1FAE5", borderColor: "#059669" } }}
                                >
                                    View Document
                                </Button>
                            </Box>
                        )}

                        {/* Upload section */}
                        <Box
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                border: "2px dashed",
                                borderColor: signedFile ? "#10B981" : "#CBD5E1",
                                borderRadius: 2,
                                p: 2.5,
                                textAlign: "center",
                                cursor: "pointer",
                                bgcolor: signedFile ? "#F0FDF4" : "#F8FAFC",
                                transition: "all 0.2s",
                                "&:hover": { borderColor: BRAND, bgcolor: "#F3EDFD" },
                            }}
                        >
                            <CloudUploadOutlinedIcon sx={{ fontSize: 32, color: signedFile ? "#10B981" : "#94A3B8", mb: 0.5 }} />
                            {signedFile ? (
                                <>
                                    <Typography fontSize="0.8rem" fontWeight={600} color="#059669">{signedFile.name}</Typography>
                                    <Typography fontSize="0.7rem" color="text.secondary">({(signedFile.size / 1024).toFixed(1)} KB) — Click to change</Typography>
                                </>
                            ) : (
                                <>
                                    <Typography fontSize="0.8rem" fontWeight={600} color="#475569">
                                        {signedCopyRow?.signed_copy_path ? "Replace signed copy" : "Upload signed copy"}
                                    </Typography>
                                    <Typography fontSize="0.7rem" color="text.secondary">Click to browse — PDF only</Typography>
                                </>
                            )}
                        </Box>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf"
                            style={{ display: "none" }}
                            onChange={handleSignedFileChange}
                        />
                        {signedUploadLoading && <LinearProgress sx={{ mt: 1.5, borderRadius: 1 }} />}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button onClick={() => { setSignedCopyRow(null); setSignedFile(null); }} variant="outlined" size="small" sx={{ textTransform: "none" }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUploadSignedCopy}
                            variant="contained" size="small"
                            disabled={!signedFile || signedUploadLoading}
                            startIcon={signedUploadLoading ? <CircularProgress size={14} color="inherit" /> : <CloudUploadOutlinedIcon />}
                            sx={{
                                textTransform: "none", fontWeight: 600,
                                bgcolor: "#10B981", "&:hover": { bgcolor: "#059669" },
                                "&:disabled": { bgcolor: "#E2E8F0" },
                            }}
                        >
                            {signedUploadLoading ? "Uploading..." : "Upload"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Admin Fee Override Approval Panel ──────────────────── */}
                {isAdmin && (
                    <Dialog open={adminApprovalOpen} onClose={() => setAdminApprovalOpen(false)} maxWidth="md" fullWidth>
                        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
                            <NotificationsActiveIcon sx={{ color: "#D97706", fontSize: 20 }} />
                            Fee Override Approval Requests
                            {approvalRequests.length > 0 && (
                                <Chip label={`${approvalRequests.length} pending`} size="small" sx={{ ml: 1, bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 700, fontSize: "0.68rem" }} />
                            )}
                        </DialogTitle>
                        <DialogContent dividers>
                            {approvalRequests.length === 0 ? (
                                <Box sx={{ py: 4, textAlign: "center" }}>
                                    <Typography color="text.secondary" fontSize="0.9rem">No pending fee override requests.</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                    {approvalRequests.map(r => (
                                        <Box key={r.id} sx={{ p: 1.5, borderRadius: 2, border: "1.5px solid #FDE68A", bgcolor: "#FFFBEB", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                                            <Box>
                                                <Typography fontSize="0.85rem" fontWeight={700} color="#92400E">
                                                    {r.requester_name || "Employee"} → {r.client_name}
                                                </Typography>
                                                <Typography fontSize="0.78rem" color="#78350F">
                                                    Custom Fee: <strong>{r.commission_pct}%</strong> ({r.fee_basis}) &nbsp;|&nbsp; Agreement: {r.period_months} months
                                                </Typography>
                                                <Typography fontSize="0.72rem" color="text.secondary">
                                                    Requested: {new Date(r.requested_at).toLocaleString()}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <Button
                                                    size="small" variant="contained"
                                                    sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#10B981", "&:hover": { bgcolor: "#059669" }, minWidth: 90 }}
                                                    onClick={() => handleRespondOverride(r.id, "approved")}
                                                >
                                                    ✓ Approve
                                                </Button>
                                                <Button
                                                    size="small" variant="outlined"
                                                    sx={{ textTransform: "none", fontWeight: 600, color: "#DC2626", borderColor: "#DC2626", "&:hover": { bgcolor: "#FEE2E2", borderColor: "#DC2626" }, minWidth: 80 }}
                                                    onClick={() => handleRespondOverride(r.id, "denied")}
                                                >
                                                    ✕ Deny
                                                </Button>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button onClick={() => setAdminApprovalOpen(false)} variant="outlined" size="small" sx={{ textTransform: "none" }}>Close</Button>
                        </DialogActions>
                    </Dialog>
                )}

            </Box>
        </Base>
    );
};

export default AtsAgreements;
